import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  useTheme,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { User } from "../../types/UserTypes";
import { useNavigate } from "react-router-dom";

type UserTableProps = {
  usersData: User[];
  maxHeight?: string | number;
  stickyHeader?: boolean;
  onSelectUser?: (user: User) => void;
};

const UserTable: React.FC<UserTableProps> = ({
  usersData,
  maxHeight = 400,
  stickyHeader = true,
  onSelectUser,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  // Client-side search with ~250ms debounce
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput), 250);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const filteredUsers = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return usersData;
    return usersData.filter((u) =>
      [`${u.firstName} ${u.lastName}`, u.email, u.role].some((field) =>
        (field ?? "").toLowerCase().includes(q)
      )
    );
  }, [usersData, debouncedSearch]);

  // --- Menú contextual (click derecho) ---
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    user: User;
  } | null>(null);

  const handleCloseContextMenu = () => setContextMenu(null);

  const handleMenuAction = (action: (user: User) => void) => {
    if (contextMenu) action(contextMenu.user);
    handleCloseContextMenu();
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.5,
        }}
      >
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t("users.search.placeholder")}
          sx={{ flexGrow: 1, maxWidth: { sm: 420 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="pSmall" color={theme.palette.neutral["600"]}>
          {t("users.search.results", { count: filteredUsers.length })}
        </Typography>
      </Box>
      <TableContainer
        component={Paper}
        sx={{ borderRadius: 2, maxHeight: maxHeight, overflow: "auto" }}
      >
        <Table stickyHeader={stickyHeader}>
          <TableHead sx={{ backgroundColor: theme.palette.neutral["200"] }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.name")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.email")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.role")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.status")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.lastConnection")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.lastUpdate")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.createdAt")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="pSmall" color={theme.palette.neutral["600"]}>
                    {t("users.search.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredUsers.map((user, index) => (
              <TableRow
                key={user.id}
                onClick={() => {
                  navigate(`/users/${user.id}`);
                  if (onSelectUser) {
                    onSelectUser(user);
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, user });
                }}
                sx={{
                  cursor: "pointer",
                  backgroundColor:
                    index % 2 === 1 ? theme.palette.neutral["100"] : "inherit",
                  transition: "background-color 0.3s ease",
                  "&:hover": {
                    backgroundColor: theme.palette.neutral["100"],
                  },
                }}
              >
                <TableCell>
                  <Typography variant="pSmall">{`${user.firstName} ${user.lastName}`}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="pSmall">{user.email}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="pSmall">{user.role}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(
                      `userStatus.${user.active ? "active" : "inactive"}`
                    )}
                    sx={{
                      backgroundColor: user.active
                        ? theme.palette.success.main
                        : theme.palette.neutral["400"],
                      color: "white",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="pSmallest">
                    {user.lastLoginAt && dayjs(user.lastLoginAt).isValid()
                      ? dayjs(user.lastLoginAt).format("MMM D, YYYY")
                      : "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="pSmallest">
                    {dayjs(user.updatedAt).isValid()
                      ? dayjs(user.updatedAt).format("MMM D, YYYY")
                      : "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="pSmallest">
                    {dayjs(user.createdAt).isValid()
                      ? dayjs(user.createdAt).format("MMM D, YYYY")
                      : "-"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() =>
            handleMenuAction((user) =>
              window.open(`/users/${user.id}`, "_blank")
            )
          }
        >
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t("campaigns.filters.openInNewTab", {
              defaultValue: "Abrir en nueva pestaña",
            })}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleMenuAction((user) => navigate(`/users/${user.id}`))
          }
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText>{t("users.view")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleMenuAction((user) => navigate(`/users/${user.id}/edit`))
          }
        >
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>{t("users.edit")}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserTable;
